// retirement.ts — Auto-retirement logic and manual retirement
import { supabase, isSupabaseConfigured } from './supabase';

/**
 * Auto-retire players based on age + performance curve.
 * Called at end of each season during archiving.
 * 
 * Rules:
 * - No forced retirement by season count
 * - Random chance starts at age 35 (low, scales up)
 * - Stars (high OVR, lots of games) get longevity bonus
 * - Bench players (low games, low OVR) retire earlier
 * - Force retire at age 42
 */
export async function processRetirements(leagueId: string, seasonYear: number): Promise<number> {
  if (!isSupabaseConfigured() || !supabase) return 0;

  // Get all teams in this league
  const { data: teams } = await supabase.from('teams').select('id').eq('league_id', leagueId);
  if (!teams || teams.length === 0) return 0;
  const teamIds = teams.map(t => t.id);

  // Get all active players on these teams
  const { data: players } = await supabase
    .from('players')
    .select('id, first_name, last_name, age, retired, team_id, position, overall')
    .in('team_id', teamIds)
    .eq('retired', false);

  if (!players || players.length === 0) return 0;

  // Get this season's games played for each player
  const playerIds = players.map(p => p.id);
  const { data: seasonStats } = await supabase
    .from('player_season_stats')
    .select('player_id, games')
    .in('player_id', playerIds)
    .eq('season_year', seasonYear);

  const gamesMap = new Map<string, number>();
  (seasonStats || []).forEach(s => gamesMap.set(s.player_id, s.games || 0));

  let retiredCount = 0;
  const toRetire: string[] = [];

  for (const player of players) {
    const age = player.age || 25;
    const overall = player.overall || 50;
    const games = gamesMap.get(player.id) || 0;
    let shouldRetire = false;

    // Force retire at 42
    if (age >= 42) {
      shouldRetire = true;
    }
    // Random retirement starts at 35
    else if (age >= 35) {
      // Base chance: 5% at age 35, +5% per year
      let chance = (age - 34) * 0.05;

      // Regular players (high OVR, lots of games) get longevity bonus
      // A star at 35 might only have 2% chance instead of 5%
      if (overall >= 80 && games >= 100) {
        chance *= 0.4; // 60% reduction — stars play longer
      } else if (overall >= 70 && games >= 80) {
        chance *= 0.6; // 40% reduction — solid regulars
      } else if (games < 30) {
        chance *= 2.0; // Bench players retire faster
      } else if (overall < 55) {
        chance *= 1.5; // Below-average players retire faster
      }

      // Cap at 80%
      chance = Math.min(chance, 0.80);

      if (Math.random() < chance) {
        shouldRetire = true;
      }
    }

    if (shouldRetire) {
      toRetire.push(player.id);
      retiredCount++;
    }
  }

  // Batch retire
  if (toRetire.length > 0) {
    const batchSize = 50;
    for (let i = 0; i < toRetire.length; i += batchSize) {
      const batch = toRetire.slice(i, i + batchSize);
      await supabase
        .from('players')
        .update({ retired: true })
        .in('id', batch);
    }
    console.log(`👴 Retired ${retiredCount} players (age 35+ curve)`);
  }

  return retiredCount;
}

/**
 * Manually retire a single player (called from UI).
 * Available after 5 seasons.
 */
export async function retirePlayerById(playerId: string): Promise<boolean> {
  if (!isSupabaseConfigured() || !supabase) return false;

  // Check minimum seasons requirement (button available after 5)
  const { data: career } = await supabase
    .from('player_career_stats')
    .select('seasons_played')
    .eq('player_id', playerId)
    .single();

  const seasonsPlayed = career?.seasons_played || 0;

  // Also count current season stats
  const { count } = await supabase
    .from('player_season_stats')
    .select('*', { count: 'exact', head: true })
    .eq('player_id', playerId);

  const totalSeasons = seasonsPlayed + (count && count > 0 ? 1 : 0);

  if (totalSeasons < 5) {
    console.log(`Player needs 5 seasons to retire (has ${totalSeasons})`);
    return false;
  }

  const { error } = await supabase
    .from('players')
    .update({ retired: true })
    .eq('id', playerId);

  if (error) {
    console.error('Retirement failed:', error.message);
    return false;
  }

  return true;
}
